<template>
  <div class="kpt-detail-content-page">
    <vue-headful :title="`${remote.name} - Devices`" />
    <div class="kpt-detail-content-actions">
      <div class="buttons" v-if="$hasRole('Super User,Kymeta Admin,Product Support')">
        <b-button type="is-primary is-outlined" size="is-small" icon-left="edit" v-if="!editing" @click="editDevices">Edit</b-button>
        <b-button type="is-primary is-outlined" size="is-small" icon-left="save" :loading="saving" :disabled="!canSave" v-if="editing" @click="saveEditingDevices">Save</b-button>
        <b-button type="is-primary is-outlined" size="is-small" icon-left="times" v-if="editing" :disabled="saving" @click="cancelEditingDevices">Cancel</b-button>
      </div>
    </div>
    <b-notification
      :active="updateErroredOut"
        type="is-danger"
        aria-close-label="Close notification"
        role="alert"
        :closable="false">
        {{error}}
    </b-notification>
    <b-notification 
       :active="isInvalid"
       type="is-danger"
       aria-close-label="Close notification"
       role="alert"
       :closable="false">
       <p>
         <strong>Invalid field<span v-if="validationErrors.length > 1">s</span></strong>: 
         <span v-for="(err, idx) in validationErrors" :key="idx">
           {{err}}<span v-if="idx != validationErrors.length - 1">, </span>
         </span>
       </p>
    </b-notification>
    <b-message type="is-light" :active="isEditDisabled && editing" >
      Cannot update the following hardware when remote is provisioned and has an active subscription : <br/>Modem serial, Modem model, BUC model, Router SN, IMEI, ICCID
    </b-message>
    <header class="kpt-device-item-header">
      <div></div>
      <div><label class="label">Model</label></div>
      <div><label class="label">Serial</label></div>
    </header>
    <div>
      <!-- Terminal -->
      <div class="kpt-remote-device-item">
        <div class="kpt-remote-device-item-label">
          <label class="label">Terminal</label>
        </div>
        <div class="kpt-remote-device-item-model">
          <span v-if="!editing">{{idToName(remoteDevices.terminalModel, getModelsForType('DEV_TRMNL', $store))}}</span>
          <b-select placeholder="Select a model" v-model="clonedDevices.terminalModel" expanded v-else>
            <option :value="null">Select a model</option>
            <option
              v-for="option in getModelsForType('DEV_TRMNL', $store)"
              :value="option.shortcode"
              :key="option.shortcode">
              {{ option.name }}
            </option>
          </b-select>
        </div>
        <div class="kpt-remote-device-item-serial">
          <span v-if="!editing">{{remoteDevices.terminalSerial}}</span>
          <b-input type="text" v-model="clonedDevices.terminalSerial" v-else />
        </div>
      </div>
      <!-- Antenna -->
      <div class="kpt-remote-device-item">
        <div class="kpt-remote-device-item-label">
          <label class="label">Antenna</label>
        </div>
        <div class="kpt-remote-device-item-model">
          <span v-if="!editing">{{idToName(remoteDevices.antenna1Model, getModelsForType('DEV_ANTNA', $store))}}</span>
          <b-select placeholder="Select a model" v-model="clonedDevices.antenna1Model" expanded v-else>
            <option :value="null">Select a model</option>
            <option
              v-for="option in getModelsForType('DEV_ANTNA', $store)"
              :value="option.shortcode"
              :key="option.shortcode">
              {{ option.name }}
            </option>
          </b-select>
        </div>
        <div class="kpt-remote-device-item-serial">
          <span v-if="!editing">{{remoteDevices.antenna1Serial}}</span>
          <b-field :type="isDuplicate('antenna1Serial') ? 'is-danger' : ''" v-else>
            <b-input type="text" v-model="clonedDevices.antenna1Serial" />
          </b-field>
        </div>
      </div>
      <!-- Modem -->
      <div class="kpt-remote-device-item">
        <div class="kpt-remote-device-item-label">
          <label class="label">Modem</label>
        </div>
        <div class="kpt-remote-device-item-model">
          <span v-if="!editing">{{idToName(remoteDevices.modemModel, getModelsForType('DEV_MODEM', $store))}}</span>
          <div v-else>
            <b-tooltip label="Modem update not allowed while Privacy Mode is enabled." type="is-warning" :active="remote.private">
              <b-select placeholder="Select a model" v-model="clonedDevices.modemModel" expanded :disabled="(remote.private && remoteDevices.modemModel) || isEditDisabled">
                <option :value="null">Select a model</option>
                <option
                  v-for="option in getModelsForType('DEV_MODEM', $store)"
                  :value="option.shortcode"
                  :key="option.shortcode">
                  {{ option.name }}
                </option>
              </b-select>
            </b-tooltip>
          </div>
        </div>
        <div class="kpt-remote-device-item-serial">
          <span v-if="!editing">{{remoteDevices.modemSerial}}</span>
          <div v-else>
            <b-tooltip label="Modem update not allowed while Privacy Mode is enabled." type="is-warning" :active="remote.private">
              <b-input type="text" v-model="clonedDevices.modemSerial" :disabled="(remote.private && remoteDevices.modemModel) || isEditDisabled"/>
            </b-tooltip>
          </div>
        </div>
      </div>
      <!-- BUC -->
      <div class="kpt-remote-device-item">
        <div class="kpt-remote-device-item-label">
          <label class="label">BUC</label>
        </div>
        <div class="kpt-remote-device-item-model">
          <span v-if="!editing">{{idToName(remoteDevices.bucModel, getModelsForType('DEV_BUC', $store))}}</span>
          <b-select placeholder="Select a model" v-model="clonedDevices.bucModel" expanded v-else :disabled="isEditDisabled">
            <option :value="null">Select a model</option>
            <option
              v-for="option in getModelsForType('DEV_BUC', $store)"
              :value="option.shortcode"
              :key="option.shortcode">
              {{ option.name }}
            </option>
          </b-select>
        </div>
        <div class="kpt-remote-device-item-serial">
          <span v-if="!editing">{{remoteDevices.bucSerial}}</span>
          <b-input type="text" v-model="clonedDevices.bucSerial" v-else />
        </div>
      </div>
      <!-- LNB -->
      <div class="kpt-remote-device-item">
        <div class="kpt-remote-device-item-label">
          <label class="label">LNB</label>
        </div>
        <div class="kpt-remote-device-item-model">
          <span v-if="!editing">{{idToName(remoteDevices.lnbModel, getModelsForType('DEV_LNB', $store))}}</span>
          <b-select placeholder="Select a model" v-model="clonedDevices.lnbModel" expanded v-else>
            <option :value="null">Select a model</option>
            <option
              v-for="option in getModelsForType('DEV_LNB', $store)"
              :value="option.shortcode"
              :key="option.shortcode">
              {{ option.name }}
            </option>
          </b-select>
        </div>
        <div class="kpt-remote-device-item-serial">
          <span v-if="!editing">{{remoteDevices.lnbSerial}}</span>
          <b-input type="text" v-model="clonedDevices.lnbSerial" v-else />
        </div>
      </div>
      <!-- Cell Router -->
      <div class="kpt-remote-device-item">
        <div class="kpt-remote-device-item-label">
          <label class="label">Cell Router</label>
        </div>
        <div class="kpt-remote-device-item-model">
          <span v-if="!editing">{{idToName(remoteDevices.cellRouterModel, getModelsForType('DEV_CLRTR', $store))}}</span>
          <b-select placeholder="Select a model" v-model="clonedDevices.cellRouterModel" expanded v-else>
            <option :value="null">Select a model</option>
            <option
              v-for="option in getModelsForType('DEV_CLRTR', $store)"
              :value="option.shortcode"
              :key="option.shortcode">
              {{ option.name }}
            </option>
          </b-select>
        </div>
        <div class="kpt-remote-device-item-serial">
          <span v-if="!editing">{{remoteDevices.cellRouterSerial}}</span>
          <b-input type="text" v-model="clonedDevices.cellRouterSerial" v-else :disabled="isEditDisabled" maxlength="14" v-cleave="cellRouterPattern.custom" @blur="blurEventHandlerCellRouterSerial($event)" PlaceHolder="ex: XXXX-XXXX-XXXX, X is a Hex digit" />
        </div>
      </div>
      <!-- Cell IMEI -->
      <div class="kpt-remote-device-item">
        <div class="kpt-remote-device-item-label">
          <label class="label">Cell Router IMEI</label>
        </div>
        <div class="kpt-remote-device-item-model">
          -
        </div>
        <div class="kpt-remote-device-item-serial">
          <span v-if="!editing">{{remoteDevices.cellRouterImei}}</span>
          <b-input type="text" v-model="clonedDevices.cellRouterImei" v-else :disabled="isEditDisabled"/>
        </div>
      </div>
      <!-- Cell ICCID -->
      <div class="kpt-remote-device-item">
        <div class="kpt-remote-device-item-label">
          <label class="label">Cell Router ICCID</label>
        </div>
        <div class="kpt-remote-device-item-model">
          -
        </div>
        <div class="kpt-remote-device-item-serial">
          <span v-if="!editing">{{remoteDevices.cellRouterIccid}}</span>
          <b-input type="text" v-model="clonedDevices.cellRouterIccid" v-else :disabled="isEditDisabled"/>
        </div>
      </div>
    </div>
    <div v-if="$hasRole('Super User,Kymeta Admin,Product Support')">
      <div class="kpt-remote-device-combiner-switch">
        <b-tooltip label="Refreshing Remote data... please wait." :active="isRefreshing">
          <b-switch :value="remoteDevices.isCombiner" @input="toggleRemoteIsCombiner" :disabled="isRefreshing">This remote has a combiner attached.</b-switch>
        </b-tooltip>
      </div>
      <div v-if="remoteDevices.isCombiner">
        <!-- plus -->
        <div class="kpt-remote-device-item">
          <div class="kpt-remote-device-item-label">
            <label class="label">Plus</label>
          </div>
          <div class="kpt-remote-device-item-model">
            <span>{{remoteDevices.mTennaPlusSerial ? 'mTenna Plus' : '-'}}</span>
          </div>
          <div class="kpt-remote-device-item-serial">
            <span v-if="!editing">{{remoteDevices.mTennaPlusSerial}}</span>
            <b-input type="text" v-model="clonedDevices.mTennaPlusSerial" v-else />
          </div>
        </div>
        <!-- select -->
        <div class="kpt-remote-device-item">
          <div class="kpt-remote-device-item-label">
            <label class="label">Select</label>
          </div>
          <div class="kpt-remote-device-item-model">
            <span>{{remoteDevices.mTennaSelectSerial ? 'mTenna Select' : '-'}}</span>
          </div>
          <div class="kpt-remote-device-item-serial">
            <span v-if="!editing">{{remoteDevices.mTennaSelectSerial}}</span>
            <b-input type="text" v-model="clonedDevices.mTennaSelectSerial" v-else />
          </div>
        </div>
        <!-- antenna 2 -->
        <div class="kpt-remote-device-item">
          <div class="kpt-remote-device-item-label">
            <label class="label">Antenna #2</label>
          </div>
          <div class="kpt-remote-device-item-model">
            <span v-if="!editing">{{idToName(remoteDevices.antenna2Model, getModelsForType('DEV_ANTNA', $store))}}</span>
            <b-select placeholder="Select a model" v-model="clonedDevices.antenna2Model" expanded v-else>
              <option :value="null">Select a model</option>
              <option 
                v-for="option in getModelsForType('DEV_ANTNA', $store)"
                :value="option.shortcode"
                :key="option.shortcode">
                {{ option.name }}
              </option>
            </b-select>
          </div>
          <div class="kpt-remote-device-item-serial">
            <span v-if="!editing">{{remoteDevices.antenna2Serial}}</span>
            <b-field :type="isDuplicate('antenna2Serial') ? 'is-danger' : ''" v-else>
              <b-input type="text" v-model="clonedDevices.antenna2Serial" />
            </b-field>
          </div>
        </div>
        <!-- antenna 3 -->
        <div class="kpt-remote-device-item">
          <div class="kpt-remote-device-item-label">
            <label class="label">Antenna #3</label>
          </div>
          <div class="kpt-remote-device-item-model">
            <span v-if="!editing">{{idToName(remoteDevices.antenna3Model, getModelsForType('DEV_ANTNA', $store))}}</span>
            <b-select placeholder="Select a model" v-model="clonedDevices.antenna3Model" expanded v-else>
              <option :value="null">Select a model</option>
              <option 
                v-for="option in getModelsForType('DEV_ANTNA', $store)"
                :value="option.shortcode"
                :key="option.shortcode">
                {{ option.name }}
              </option>
            </b-select>
          </div>
          <div class="kpt-remote-device-item-serial">
            <span v-if="!editing">{{remoteDevices.antenna3Serial}}</span>
            <b-field :type="isDuplicate('antenna3Serial') ? 'is-danger' : ''" v-else>
              <b-input type="text" v-model="clonedDevices.antenna3Serial" />
            </b-field>
          </div>
        </div>
        <!-- antenna 4 -->
        <div class="kpt-remote-device-item">
          <div class="kpt-remote-device-item-label">
            <label class="label">Antenna #4</label>
          </div>
          <div class="kpt-remote-device-item-model">
            <span v-if="!editing">{{idToName(remoteDevices.antenna4Model, getModelsForType('DEV_ANTNA', $store))}}</span>
            <b-select placeholder="Select a model" v-model="clonedDevices.antenna4Model" expanded v-else>
              <option :value="null">Select a model</option>
              <option 
                v-for="option in getModelsForType('DEV_ANTNA', $store)"
                :value="option.shortcode"
                :key="option.shortcode">
                {{ option.name }}
              </option>
            </b-select>
          </div>
          <div class="kpt-remote-device-item-serial">
            <span v-if="!editing">{{remoteDevices.antenna4Serial}}</span>
            <b-field :type="isDuplicate('antenna4Serial') ? 'is-danger' : ''" v-else>
              <b-input type="text" v-model="clonedDevices.antenna4Serial" />
            </b-field>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
<script>
import { isEqual } from 'lodash';
import { mapMutations } from 'vuex';
import { idToNameParserMixin } from '../../shared/mixins/idToNameParser';
import { getModelsForTypeMixin } from '../../shared/mixins/getModelForDeviceTypeMixin';
  import { validateCellRouterSerialMixin } from '../../shared/mixins/validateCellRouterSerialMixin'; // Cleave.js

export default {
  data() {
    return {
      editing: false,
      saving: false,
      clonedDevices: null,
      duplicateFields: [],
      validationErrors: [],
      cellRouterPattern: {       
        custom: {
          delimiters: ['-', '-'],
          blocks: [4, 4, 4],
          numeral: false,
        }
      }
    }
  },
  computed: {
    remote() {
      return this.$store.state.remotes.remote;
    },
    remoteDevices() {
      return this.remote.devices || {};
    },
    isRefreshing() {
      return this.$store.state.remotes.remoteRefreshing;
    },
    deviceModels() {
      return this.$store.state.devices.models;
    },
    deviceTypes() {
      return this.$store.state.devices.types;
    },
    canSave() {
      if (!this.editing) return false;
      if (isEqual(this.clonedDevices, this.remoteDevices)) return false;
      if (this.saving) return false;
      return true;
    },
    error() {
      return this.$store.state.remotes.remoteDevicesError;
    },
    updateErroredOut() {
      return this.error != null;
    },
    isInvalid() {
      return this.validationErrors.length > 0;
    },
    isEditDisabled() {
      return ((this.remote?.subscriptions?.filter(s => s.active)?.length > 0) // remote has an active subscription 
      && this.remote?.provisioned // remote kymeta provisioned
      && this.remote?.satellite?.provisioned); // remote network provisioned
    }
  },
    mixins: [idToNameParserMixin, getModelsForTypeMixin, validateCellRouterSerialMixin],
  methods: {
    ...mapMutations([
      'toggleRemoteIsCombiner' // map `this.toggleRemoteIsCombiner()` to `this.$store.commit('toggleRemoteIsCombiner')`
    ]),
    editDevices() {
      this.clonedDevices = JSON.parse(JSON.stringify(this.remoteDevices));
      this.editing = true;
      // KyAnalytics client plugin, event tracker
      this.$kya.event('remotes', 'showEditRemoteDevices', 'Edititing remote device.', { remoteId: this.remote.id });
    },
    cancelEditingDevices() {
      this.clonedDevices = null;
      this.editing = false;
      this.validationErrors = [];
      // KyAnalytics client plugin, event tracker
      this.$kya.event('remotes', 'hideEditRemoteDevices', 'Cancelled edit device.', { remoteId: this.remote.id });
    },
    async saveEditingDevices() {      
      this.validationErrors = [];
      // validate modem and antenna model entry
      if (this.clonedDevices.modemSerial && !this.clonedDevices.modemModel) { this.validationErrors.push('Modem Model') }
      if (this.clonedDevices.antenna1Serial && !this.clonedDevices.antenna1Model) { this.validationErrors.push('Antenna Model') }
      if (this.clonedDevices.cellRouterSerial && !this.clonedDevices.cellRouterModel) { this.validationErrors.push('Cell Router Model') }
      if (this.clonedDevices.cellRouterSerial && !this.validCellRouterSerial(this.clonedDevices.cellRouterSerial)) { this.validationErrors.push('Cell Router Serial (ex: XXXX-XXXX-XXXX, X is a Hex digit)') }
      if (this.validationErrors.length) return;

      this.duplicateFields = [];
      // validate serials
      // get serial values first
      const serialKeys = [];
      Object.keys(this.clonedDevices).forEach(key => {
        if (key.toLowerCase().includes('serial') && key.toLowerCase().includes('antenna')) serialKeys.push({ key, value: this.clonedDevices[key] });
      })
      serialKeys.forEach(serialKey => {
        const duplicates = serialKeys.filter(k => k.value == serialKey.value);
        if (duplicates.length > 1) this.duplicateFields.push(serialKey.key);
      })
      if (this.duplicateFields.length) return;

      this.saving = true;
      const result = await this.$store.dispatch('updateRemoteDevices', { id: this.remote.id, devices: this.clonedDevices });
      this.saving = false;
      if (result) {
        this.editing = false;
        this.clonedDevices = null;
      }
      // KyAnalytics client plugin, event tracker
      this.$kya.event('remotes', 'updateRemoteDevices', 'Save edit device clicked.', { remoteId: this.remote.id });
    },
    isDuplicate(fieldName) {
      return this.duplicateFields.includes(fieldName);
    },
    validCellRouterSerial(serial) {
      const re = /^(?:[A-F0-9]{4}-){2}[A-F0-9]{4}$/;
      return re.test(serial);
    },
    blurEventHandlerCellRouterSerial(e) {
      const val = e.target.value;
      this.clonedDevices.cellRouterSerial = val;
    }
  }
}
</script>
<style lang="scss" scoped>
@import '../../styles/mixins';
@import '../../styles/colors';

header.kpt-device-item-header {
  @include display-grid;
  grid-template-columns: 200px 250px 300px;
  gap: 15px
}

.kpt-detail-content-page {
  ::v-deep select {
    option:first-of-type {
      color: $grey-light;
    }
  }
}

.kpt-remote-device-item {
  @include display-grid;
  grid-template-columns: 200px 250px 300px;
  gap: 15px;
  border-bottom: 1px solid $grey-lighter;
  padding: 6px 0;

  &:last-child {
    border-bottom: 0px;
  }

  .kpt-remote-device-item-label {
    display: flex;
    align-items: center;
  }
  .b-tooltip {
    width: 100%;
  }
}

.kpt-remote-device-combiner-switch {
  margin: 12px 0;
}
</style>
